import { Box, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { useMemo } from "react";
import Flex from "@/components/Flex";
import {
  HOME_CARE_ROS_DETAILS_COLUMN_SPAN,
  HOME_CARE_ROS_DETAILS_MIN_WIDTH,
  HOME_CARE_ROS_DETAILS_PLACEHOLDER,
} from "../home-care-ros-constant";
import {
  homeCareRosOptionLabel,
  parseHomeCareRosOptions,
  parseHomeCareRosSelection,
  serializeHomeCareRosSelection,
} from "../home-care-ros-utils";

export interface HomeCareRosOptionsProps {
  catalog: string;
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  sx?: SxProps<Theme>;
}

const HomeCareRosOptions = ({
  catalog,
  value,
  onChange,
  disabled,
  readOnly,
  sx,
}: HomeCareRosOptionsProps) => {
  const options = useMemo(() => parseHomeCareRosOptions(catalog), [catalog]);
  const selection = useMemo(() => parseHomeCareRosSelection(value), [value]);

  const updateSelection = (next: typeof selection) =>
    onChange(serializeHomeCareRosSelection(options, next));

  const handleToggle = (label: string, isChecked: boolean) => {
    if (readOnly) return;
    const { [label]: _removed, ...rest } = selection;
    updateSelection(isChecked ? { ...rest, [label]: "" } : rest);
  };

  return (
    <Box
      display="grid"
      columnGap={2}
      gridTemplateColumns="repeat(auto-fill, minmax(180px, 1fr))"
      sx={sx}
    >
      {options.map(({ label, hasFreeText }) => {
        const isChecked = label in selection;
        const displayLabel = homeCareRosOptionLabel(label);
        const hasDetails = hasFreeText && isChecked;
        return (
          <Flex
            key={label}
            alignItems="center"
            minWidth={0}
            gridColumn={
              hasDetails ? HOME_CARE_ROS_DETAILS_COLUMN_SPAN : undefined
            }
          >
            <FormControlLabel
              label={displayLabel}
              disabled={disabled}
              sx={{ mr: 1 }}
              control={
                <Checkbox
                  size="small"
                  checked={isChecked}
                  onChange={(_, checked) => handleToggle(label, checked)}
                />
              }
            />
            {hasDetails && (
              <TextField
                size="small"
                sx={{ flex: 1, minWidth: HOME_CARE_ROS_DETAILS_MIN_WIDTH }}
                disabled={disabled}
                placeholder={HOME_CARE_ROS_DETAILS_PLACEHOLDER}
                value={selection[label] ?? ""}
                onChange={(e) =>
                  updateSelection({ ...selection, [label]: e.target.value })
                }
                inputProps={{
                  "aria-label": `${displayLabel} details`,
                  readOnly,
                }}
              />
            )}
          </Flex>
        );
      })}
    </Box>
  );
};

export default HomeCareRosOptions;
